import { Button, Popover, Text } from "@mantine/core";
import { IconChevronDown, IconWorld } from "@tabler/icons-react";
import { useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { RootState } from "../../store";
import { Country } from "../../types";
import { countryFlag, CountryPickerPanel } from "./country-picker-panel";
import { useCountrySelection } from "./country-selection.context";
import styles from "./country-selector.module.css";

export function CountrySelector({ fullWidth = false }: { fullWidth?: boolean }) {
  const currentCountry: Country | null = useSelector(
    (state: RootState) => state.userMetaData.clientCountry,
  );
  const {
    countries,
    popularCountries,
    directoryLoading,
    selectCountry,
  } = useCountrySelection();
  const [pickerOpened, setPickerOpened] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleCountrySelection = (country: Country) => {
    selectCountry(country);
    setPickerOpened(false);
    if (location.search) {
      navigate({ pathname: location.pathname, search: "" }, { replace: true });
    }
  };

  return (
    <Popover
      opened={pickerOpened}
      onChange={setPickerOpened}
      width={390}
      position="bottom-end"
      shadow="lg"
      withinPortal
    >
      <Popover.Target>
        <Button
          variant="default"
          color="dark"
          radius="md"
          fullWidth={fullWidth}
          className={styles.selectorButton}
          onClick={() => setPickerOpened(!pickerOpened)}
          leftSection={
            currentCountry ? (
              <span className={styles.flag}>{countryFlag(currentCountry.code)}</span>
            ) : (
              <IconWorld size={18} />
            )
          }
          rightSection={<IconChevronDown size={15} />}
          aria-label="Change country"
        >
          <Text size="sm" fw={600} truncate>
            {currentCountry?.countryName ?? "Choose country"}
          </Text>
        </Button>
      </Popover.Target>
      <Popover.Dropdown p="md" className={styles.popover}>
        <CountryPickerPanel
          countries={countries}
          popularCountries={popularCountries}
          selectedCode={currentCountry?.code}
          onChange={handleCountrySelection}
          loading={directoryLoading}
          compact
        />
      </Popover.Dropdown>
    </Popover>
  );
}
